import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDrag, useDrop, DragElementWrapper, DragSourceOptions } from 'react-dnd';
import { useDispatch } from 'react-redux';
import ReactTable, { ExpandedRows } from 'react-table-6';
import { HasClassName } from '../../component-utils';
import { useSelector } from '../../hook-utils';
import { CallType, EMPTY_GUID, Media, MediaType, QueueStats, Region, Task, TaskItem, TaskItemAction } from '../../model';
import { actions } from '../../store';
import { selectDateFormating, selectMyRegion, selectMyself, selectTaskStates, selectConfigProp } from '../../store/reducers/config';
import { MediaTypesFilter, taskSearchFilters } from '../../store/reducers/localSettings';
import { selectQueue } from '../../store/reducers/queues';
import { selectTaskByItemId, selectTaskItems, selectTasks } from '../../store/reducers/tasks';
import { TableColumn, TableRow } from '../../table-utils';
import { formatRelativeDateTime, getUtcNow } from '../AgentsTable';
import Button from '../Button';
import Checkbox from '../Checkbox';
import { ReactComponent as DragIcon } from '../icons/drag_handle.svg';
import { ReactComponent as EditIcon } from '../icons/edit.svg';
import { ReactComponent as CopyIcon } from '../icons/toolbar/copy.svg';
import { ReactComponent as MediaCallIcon } from '../icons/media/call.svg';
import { ReactComponent as MediaCallBackIcon } from '../icons/media/callback.svg';
import { ReactComponent as MediaEmailIcon } from '../icons/media/email.svg';
import { ReactComponent as MediaFacebookIcon } from '../icons/media/facebook.svg';
import { ReactComponent as MediaTweatIcon } from '../icons/media/twitter.svg';
import { ReactComponent as MediaChatIcon } from '../icons/media/web-chat.svg';
import MediaIcon, { MediaItemIconType } from '../MediaIcon';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from '../Menu';
import MoreButton from '../MoreButton';
import { ItemSelected, RenderSelectionPopup } from '../SearchPage';
import './TasksPage.scss';
import { selectMedia } from '../../store/reducers/media';

const DragItemType = 'task-media'

interface DragItem {
  type: typeof DragItemType
  mediaId: string
  itemId?: string
  taskId: string
}

interface Props {
  mediaId: string
}

enum TaskState {
  open = 1,
  closed = 2,
}

export default function TasksPage({ mediaId }: Props) {
  const media = useSelector(s => selectMedia(s, mediaId))
  const [menu, setMenu] = useState<TaskMenuState>()
  const tasks = useSelector(s => media && selectTasks(s, media.id, media.clidObjectKey))
  const dispatch = useDispatch()
  const format = useSelector(selectDateFormating)
  const taskStates = useSelector(selectTaskStates)

  const [expanded, setExpanded] = useState<ExpandedRows>({});
  const types = useSelector(taskSearchFilters)
  const [stateId, setStateId] = useState<number>(TaskState.open)
  const [dateLimit, setDateLimit] = useState<number>(0)

  const mediaQueueTwitter = useSelector(s => selectConfigProp(s, 'mediaQueueTwitter'))
  const mediaQueueFacebook = useSelector(s => selectConfigProp(s, 'mediaQueueFacebook'))
  const mediaQueueEmail = useSelector(s => selectConfigProp(s, 'mediaQueueEmail'))
  const mediaQueueWebChat = useSelector(s => selectConfigProp(s, 'mediaQueueWebChat'))

  const clidObjectKey = media && media.clidObjectKey
  useEffect(() => {
    if (!tasks) {
      dispatch(actions.fetchMediaTasksRequest(mediaId, clidObjectKey ?? ''))
    }
  }, [dispatch, mediaId, clidObjectKey, tasks])

  // collapse on different contact
  useEffect(() => {
    setExpanded({})
  }, [clidObjectKey])

  const dragItem: DragItem = { type: DragItemType, mediaId: mediaId, taskId: EMPTY_GUID }
  const [, dragRef] = useDrag({
    item: dragItem,
  })

  if (!format || !taskStates || !media) return null

  function onEdit(taskId: string, subject: string | null) {
    if (!clidObjectKey) return

    dispatch(actions.updateMediaTaskSubjectRequest(clidObjectKey, taskId, subject === null ? '' : subject))
  }

  function onClick(lookupId: string) {
    function listener(e: ClipboardEvent) {
      if (e.clipboardData === null) return
      e.clipboardData.setData('text/plain', lookupId)
      e.preventDefault()
    }
    document.addEventListener('copy', listener)
    document.execCommand('copy')
    document.removeEventListener('copy', listener)
  }

  const columns: TableColumn<Task>[] = [{
    id: 'expander',
    expander: true,
  }, {
    id: 'subject',
    Cell: ({ original: data }: TableRow<Task>) => data.taskId === EMPTY_GUID
      ? data.subject
      : <EditableText type='input' iconClassName='editIcon' value={data.subject} onSave={text => onEdit(data.taskId, text)} icon={true} />,
  }, {
    id: 'lookupId',
    accessor: r => {
      return <div title={r.lookupId} className='nowrap lookupId'>
        <span>{r.lookupId === '' ? '' : '#' + r.lookupId}</span>
        {r.lookupId !== '' && <button onClick={() => onClick(r.lookupId)} title='Copy to clipboard'><CopyIcon /></button>}
      </div>
    },
    width: 100,
  }, {
    id: 'date',
    accessor: r => {
      const d = formatRelativeDateTime(r.createdAtUtc, format.shortDateFormat, format.shortTimeFormat)
      return r.taskId !== EMPTY_GUID ? <div title={d} className='nowrap'>{d}</div> : null
    },
    width: 130,
  }, {
    id: 'actions',
    Cell: ({ original: item }: TableRow<Task>) => <div className='actions'>
      <MoreButton onClick={e => {
        e.stopPropagation()
        setMenu({ target: targetFromEvent(e), item, contactId: media.clidObjectKey })
      }} />
    </div>,
    width: 40,
    sortable: false,
  }]

  function onMediaType(type: keyof typeof types, value: boolean) {
    dispatch(actions.updateTaskFilter({ ...types, [type]: value }))
  }

  const closed = taskStates.find(t => t.name === "Closed")
  const icon1 = <MediaCallIcon />
  const icon2 = <MediaEmailIcon />
  const icon3 = <MediaChatIcon />
  const icon4 = <MediaFacebookIcon />
  const icon5 = <MediaTweatIcon />
  const icon6 = <MediaCallBackIcon />
  return <div className='TaskPage'>
    <div className='filter-header'>
      {closed && <div className='filters'>
        <h2>Tasks</h2>
        <div className='types'>
          <Checkbox title={closed.stateId !== stateId ? 'Only show closed tasks' : 'Only show open tasks'} label={closed.name} onChange={(e) => {
            e.currentTarget.checked ? setStateId(TaskState.closed) : setStateId(TaskState.open)
          }
          } />
          <Checkbox title={dateLimit === 1 ? 'Only show tasks less than 60 days old' : 'Only show tasks older than 60 days'} label='> 60d' onChange={(e) => {
            e.currentTarget.checked ? setDateLimit(1) : setDateLimit(0)
          }
          } />
        </div>
      </div>}
      <div className='filters'>
        <h2>Media type</h2>
        <div className='types'>
          <MediaTypeCheckbox title={types['CALL'] ? 'Exclude phone calls in task history' : 'Include phone calls in task history'} types={types} type='CALL' icon={icon1} onChange={onMediaType} />
          <MediaTypeCheckbox title={types['VM'] ? 'Exclude callbacks calls in task history' : 'Include callbacks in task history'} types={types} type='VM' icon={icon6} onChange={onMediaType} />
          {mediaQueueEmail && <MediaTypeCheckbox title={types['EMAIL'] ? 'Exclude emails in task history' : 'Include emails in task history'} types={types} type='EMAIL' icon={icon2} onChange={onMediaType} />}
          {mediaQueueWebChat && <MediaTypeCheckbox title={types['TC'] ? 'Exclude web chats in task history' : 'Include web chats in task history'} types={types} type='TC' icon={icon3} onChange={onMediaType} />}
          {mediaQueueFacebook && <MediaTypeCheckbox title={types['FB'] ? 'Exclude facebook in task history' : 'Include facebook in task history'} types={types} type='FB' icon={icon4} onChange={onMediaType} />}
          {mediaQueueTwitter && <MediaTypeCheckbox title={types['TWEET'] ? 'Exclude tweets in task history' : 'Include tweets in task history'} types={types} type='TWEET' icon={icon5} onChange={onMediaType} />}
        </div>
      </div>
    </div>
    <ReactTable
      className='tasksTable'
      TheadComponent={() => null}
      data={tasks && tasks.filter(t => (stateId === -1 || t.stateId === stateId || t.stateId === -1) && ((dateLimit === 0 && t.createdAtUtc >= dayjs(getUtcNow()).add(-60, 'day').valueOf()) || (dateLimit === 1 && t.createdAtUtc <= dayjs(getUtcNow()).add(-60, 'day').valueOf()) || t.taskId === EMPTY_GUID))}
      loading={!tasks}
      expanded={expanded}
      onExpandedChange={ex => setExpanded(ex)}
      columns={columns}
      showPagination={false}
      NoDataComponent={() => null}
      TrGroupComponent={TaskTrGroup}
      getTrGroupProps={(_: any, row?: TableRow<Task>) => row ? { taskId: row.original.taskId } : {}}
      minRows={0} defaultPageSize={9999999} SubComponent={(row: TableRow<Task>) =>
        <ItemsTable mediaId={media.id} contactId={media.clidObjectKey} task={row.original} types={types} />
      } />
    {menu && <TaskMenu state={menu} onClose={() => setMenu(undefined)} />}
    <UnlinkedMediaMediaTypeCheckbox media={media} dragRef={dragRef} />

  </div>
}

function UnlinkedMediaMediaTypeCheckbox({ media, dragRef }: { media: Media, dragRef: DragElementWrapper<DragSourceOptions> }) {
  const region = useSelector(selectMyRegion)
  const queue = useSelector(s => selectQueue(s, { id: media.queueId, server: media.server }))
  const dispatch = useDispatch()

  useEffect(() => {
    if (media.isQueueCall && !queue) {
      dispatch(actions.fetchQueuesByIds([{ id: media.queueId, server: media.server }]))
    }
  }, [dispatch, media.isQueueCall, media.queueId, media.server, queue])

  if (!region) return null

  return <section className='unlinked'>
    {media.clidObjectKey !== '' && <h2>Unlinked media</h2>}
    {media.clidObjectKey !== '' && media.taskId === '' && <div className='untracked' ref={dragRef}>
      <div className='dragIcon'>
        <DragIcon />
      </div>
      <MediaIcon className='icon' mediaType={media.mediaType} iconType={MediaItemIconType.white} />
      <div className='description'>{untrackedMediaDescription(media, region, queue)}</div>
      <Button className='createNew' primary onClick={() => dispatch(actions.createNewMediaTask(media.id, media.mediaType, media.clidObjectKey))}>+ Add Task</Button>
    </div>}
  </section>
}

function MediaTypeCheckbox({ types, type, onChange, label, icon, title }: { types: MediaTypesFilter, type: MediaType, onChange(type: MediaType, value: boolean): void, label?: string, icon?: JSX.Element, title?: string }) {
  return <Checkbox checked={types[type]} label={label} icon={icon} title={title} onChange={e => onChange(type, e.currentTarget.checked)} />
}

function TaskTrGroup({ className, taskId, ...props }: HasClassName & { taskId: string }) {
  const dispatch = useDispatch()
  const [, dropRef] = useDrop({
    accept: DragItemType,
    canDrop(item: DragItem) {
      return taskId !== item.taskId
    },
    drop({ mediaId, itemId }: DragItem, monitor) {
      dispatch(actions.moveMediaItemTask(mediaId, taskId, itemId))
    },
  })

  return <div className={classNames('rt-tr-group', className)} {...props} ref={dropRef} />
}

export function untrackedMediaDescription(media: Media, region: Region, queue?: QueueStats) {
  switch (media.mediaType) {
    case 'VM':
    case 'CALL': {
      if (media.isQueueCall) {
        return (!queue || !queue.description ? 'Incoming Queue Call' : 'Incoming ' + queue.description + ' Call') + (media.clid === '' ? '' : ' from ' + media.clid)
      } else if (media.callType === CallType.Incoming) {
        return 'Incoming Call' + (media.clid === '' ? '' : ' from ' + media.clid)
      } else {
        return 'Outgoing  Call' + (media.clid === '' ? '' : ' to ' + media.clid)
      }
    }

    default:
      return media.subject
  }
}

function getTaskItemColumns(shortDateFormat: string, shortTimeFormat: string, onMore: (state: TaskItemMenuState) => void): Array<TableColumn<TaskItem>> {
  return [{
    id: 'mediaTypeId',
    accessor: r => r.mediaTypeId,
    width: 50,
    Cell: ({ original: data }: TableRow<TaskItem>) => <MediaIcon mediaType={data.mediaTypeId} className='grid-icon' />,
  }, {
    id: 'subject',
    accessor: r => r.subject,
  }, {
    id: 'dateTimeUtc',
    accessor: r => {
      return dayjs(r.dateTimeUtc).format(shortDateFormat + ' ' + shortTimeFormat)
    },
    width: 130,
  }, {
    id: 'actions',
    Cell: ({ original: data }: TableRow<TaskItem>) => <div className='actions'>
      <MoreButton onClick={e => {
        e.stopPropagation()
        onMore({ target: targetFromEvent(e), item: data })
      }} />
    </div>,
    width: 40,
    sortable: false,
  }]
}

function ItemsTable({ mediaId, contactId, task, types }: { mediaId: string, contactId: string, task: Task, types: MediaTypesFilter }) {
  const [menu, setMenu] = useState<TaskItemMenuState>()
  const contactAndTaskId = useMemo(() => ({ mediaId, contactId, taskId: task.taskId }), [mediaId, contactId, task.taskId])
  const items = useSelector(s => selectTaskItems(s, contactAndTaskId))
  const dispatch = useDispatch()
  const format = useSelector(selectDateFormating)
  const [selected, setSelected] = useState<TaskItem | undefined>(undefined)

  useEffect(() => {
    if (!items) {
      dispatch(actions.fetchMediaTaskItemsRequest(mediaId, task.taskId))
    }
  }, [contactId, dispatch, mediaId, task.taskId, items])

  const filteredItems = useMemo(() => items && items.filter(i => Object.entries(types).filter(([t, v]) => v).map(([t, v]) => t).includes(i.mediaTypeId)), [items, types])

  if (!format) return null

  return <>
    <ReactTable
      className='itemsTable'
      TheadComponent={() => null}
      data={filteredItems}
      loading={!items}
      columns={getTaskItemColumns(format.shortDateFormat, format.shortTimeFormat, setMenu)}
      TrComponent={TaskItemTr}
      getTrProps={(_: any, row?: TableRow<TaskItem>) => row ? { mediaId: mediaId, itemId: row.original.itemId, taskId: task.taskId } : {}}
      NoDataComponent={() => null}
      showPagination={false} minRows={0} defaultPageSize={9999999} />
    {menu && <TaskItemMenu state={menu} onClose={() => setMenu(undefined)} onOpen={(item) => {
      setSelected(item)
    }} />}

    {selected && <RenderSelectionPopup selection={convert(selected)} onClose={() => setSelected(undefined)} />}
  </>
}

function convert(item: TaskItem) {
  return { type: item.mediaTypeId, id: item.itemId, isOutgoing: (item.action === TaskItemAction.outgoing) } as ItemSelected
}

function TaskItemTr({ className, children, mediaId, taskId, itemId, ...props }: React.PropsWithChildren<HasClassName> & { mediaId: string, taskId: string, itemId: string }) {
  const dragItem: DragItem = { type: DragItemType, mediaId, taskId, itemId }
  const [, dragRef, previewRef] = useDrag({
    item: dragItem,
  })

  return <div className={classNames('rt-tr', className)} {...props} ref={previewRef}>
    <div className='dragIcon' ref={dragRef}>
      <DragIcon />
    </div>
    {children}
  </div>
}

interface TaskMenuState {
  target: MenuTarget
  item: Task
  contactId: string
}

function TaskMenu({ state: { item, target, contactId }, onClose }: { state: TaskMenuState, onClose(): void }) {
  const dispatch = useDispatch()
  const unclassified = item.taskId === EMPTY_GUID
  const mySelf = useSelector(selectMyself)
  const taskStates = useSelector(selectTaskStates)

  if (!taskStates || !mySelf) return null

  const items = taskStates.map(s => <MenuItem key={'st' + s.stateId} disabled={s.stateId === item.stateId || unclassified} onClick={() => {
    dispatch(actions.updateMediaTaskStateRequest(contactId, item.taskId, s.stateId))
  }}>
    {s.name}
  </MenuItem>)

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {items}
    <MenuDivider />
    <MenuItem disabled={unclassified} onClick={() => {
      dispatch(actions.deleteMediaTaskRequest(contactId, item.taskId))
    }}>
      Delete
    </MenuItem>
  </Menu>
}

interface TaskItemMenuState {
  target: MenuTarget
  item: TaskItem
}

function TaskItemMenu({ state: { item, target }, onClose, onOpen }: { state: TaskItemMenuState, onClose(): void, onOpen(item: TaskItem): void }) {
  const dispatch = useDispatch()
  const task = useSelector(s => selectTaskByItemId(s, item.itemId))

  if (!task) return null

  const unclassified = task.taskId === EMPTY_GUID

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    <MenuItem onClick={() => {
      onOpen(item)
    }}>
      View
    </MenuItem>
    <MenuItem disabled={unclassified} onClick={() => {
      dispatch(actions.deleteMediaItemTask(item.itemId))
    }}>
      Remove
    </MenuItem>
  </Menu>
}

export interface EditableTextProps extends HasClassName {
  iconClassName?: string
  editing?: boolean
  value: string
  onSave(value: string): void
  onBlur?(): void
  editable?: boolean
  type: 'textarea' | 'input'
  icon: boolean
  onEdit?(value: boolean): void
  hightlight?: string
}

export function EditableText({ className, iconClassName, value, onSave, editing, editable, onBlur, type, icon, onEdit, hightlight }: EditableTextProps) {
  const [isEditing, setEditing] = useState(false)
  const [text, setText] = useState(value)
  const canEdit = (editable === undefined || editable === true)

  useEffect(() => {
    setText(value)
  }, [value])

  function blur() {
    if (text !== value) {
      onSave(text)
    }
    if (onBlur)
      onBlur()

    setEditing(false)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
      blur();
      setEditing(false)
    } else if (e.nativeEvent.code === 'Escape') {
      setText(value)
      setEditing(false)
    }
  }

  const onInputRef = useCallback((input: HTMLInputElement | null) => {
    if (input) {
      input.setSelectionRange(0, input.value.length)
    }
  }, [])

  const onTextAreaRef = useCallback((input: HTMLTextAreaElement | null) => {
    if (input) {
      input.setSelectionRange(0, input.value.length)
    }
  }, [])

  const input = type === 'textarea' ? <textarea value={text} onChange={e => setText(e.currentTarget.value)} onBlur={blur} autoFocus ref={onTextAreaRef} onKeyDown={onKeyDown} /> :
    <input value={text} onChange={e => setText(e.currentTarget.value)} onBlur={blur} autoFocus ref={onInputRef} onKeyDown={onKeyDown} />

  const reg = hightlight && new RegExp(hightlight, 'gi')
  const html = hightlight && reg ? value.replace(reg, '<span class="highlight">$&</span>') : value

  return <div className={classNames('EditableText', className)} onClick={() => {
    if (canEdit) {
      setEditing(true)

      if (onEdit) {
        onEdit(true)
      }
    }
  }}>{
      (isEditing || editing) && canEdit
        ? input
        : <>
          <div title={text} className='nowarp' dangerouslySetInnerHTML={{ __html: html }}></div>
          {canEdit && icon && <EditIcon className={iconClassName} />}
        </>
    }</div>
}
